/**
 * BranchSelector — Selector de sucursal activa para roles multi-sucursal.
 * Aparece en el TopBar para roles: agendador, soporte_tecnico, admin_zone, super_admin.
 * La sucursal seleccionada se persiste en localStorage y expone un evento global
 * "afc:branch-change" que las páginas escuchan para filtrar sus listas.
 */
import { useState, useEffect, useRef } from "react";
import { Building2, ChevronDown, Check, Globe2 } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";

const STORAGE_KEY = "afc_active_branch_id";
const ROLES_WITH_SELECTOR = ["super_admin", "admin_zone", "agendador", "soporte_tecnico", "supervisor", "atc"];

export function getActiveBranchId() {
  try {
    const v = localStorage.getItem(STORAGE_KEY) || "";
    // v9.3.1 · Detectar y limpiar UUIDs legados (el BranchSelector ahora usa Odoo integer IDs)
    if (v && v.includes("-") && v.length > 10) {
      localStorage.removeItem(STORAGE_KEY);
      return "";
    }
    return v;
  } catch {
    return "";
  }
}

export function setActiveBranchId(id) {
  try {
    if (id) localStorage.setItem(STORAGE_KEY, id);
    else localStorage.removeItem(STORAGE_KEY);
    window.dispatchEvent(new CustomEvent("afc:branch-change", { detail: { branch_id: id } }));
  } catch { /* noop */ }
}

export default function BranchSelector() {
  const { user } = useAuth();
  const [branches, setBranches] = useState([]);
  const [activeBranchId, setActiveState] = useState(() => getActiveBranchId());
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!user) return;
    if (!ROLES_WITH_SELECTOR.includes(user.role)) return;
    // v9.3.1 · Cargar sucursales desde filter-options (Odoo IDs enteros)
    // para que el branch_id emitido sea compatible con master_ot.branch_office_id
    api.get("/v1/ot_smart/filter-options")
      .then(({ data }) => {
        const odoo = (data.branches || []).map((b) => ({
          id: String(b.id),
          name: b.name,
          code: (b.name || "").toLowerCase().replace(/\s+/g, "_"),
        }));
        if (odoo.length) {
          setBranches(odoo);
        } else {
          // Fallback a config/branches si no hay datos OT
          api.get("/v1/config/branches")
            .then(({ data: d2 }) => setBranches(d2.items || []))
            .catch(() => setBranches([]));
        }
      })
      .catch(() => {
        api.get("/v1/config/branches")
          .then(({ data }) => setBranches(data.items || []))
          .catch(() => setBranches([]));
      });
  }, [user]);

  useEffect(() => {
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    if (open) document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [open]);

  if (!user || !ROLES_WITH_SELECTOR.includes(user.role) || branches.length === 0) return null;

  const active = branches.find((b) => b.id === activeBranchId);
  const label = active ? active.name : "Todas las sucursales";

  const pick = (id) => {
    setActiveBranchId(id);
    setActiveState(id);
    setOpen(false);
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((v) => !v)}
        className="neu-btn"
        style={{ padding: "8px 14px", fontSize: 12, fontWeight: 600 }}
        title="Cambiar sucursal activa"
        data-testid="branch-selector-btn"
      >
        <Building2 size={14} style={{ color: "#0A3747" }} />
        <span className="hidden sm:inline max-w-[140px] truncate uppercase tracking-wide" style={{ color: "#0A3747" }}>{label}</span>
        <ChevronDown size={12} className={`transition ${open ? "rotate-180" : ""}`} style={{ color: "#0A3747" }} />
      </button>

      {open && (
        <div
          data-state="open"
          className="afc-zoom-in absolute right-0 top-full mt-2 w-64 overflow-hidden z-50"
          style={{
            background: "var(--bg-surface)",
            borderRadius: 16,
            boxShadow: "-8px -8px 20px var(--neu-light), 10px 10px 24px rgba(163,177,198,0.55), 0 20px 40px -18px rgba(10,55,71,0.25)",
          }}
          data-testid="branch-selector-dropdown"
        >
          <div className="px-4 py-2.5" style={{ borderBottom: "1px solid var(--border)" }}>
            <div className="text-[10px] uppercase font-semibold tracking-widest" style={{ color: "#6B7000" }}>Sucursal activa</div>
          </div>
          <div className="max-h-72 overflow-y-auto p-1">
            <button
              onClick={() => pick("")}
              className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-lg transition"
              style={{ color: "#0A3747" }}
              onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(224,230,33,0.14)")}
              onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              data-testid="branch-option-all"
            >
              <Globe2 size={14} style={{ color: "#0A3747" }} />
              <span className="flex-1 font-medium">Todas las sucursales</span>
              {!activeBranchId && <Check size={14} style={{ color: "#0F766E" }} />}
            </button>
            {branches.map((b) => (
              <button
                key={b.id}
                onClick={() => pick(b.id)}
                className="w-full flex items-center gap-2 px-3 py-2 text-left text-sm rounded-lg transition"
                style={{ color: "#0A3747" }}
                onMouseEnter={(e) => (e.currentTarget.style.background = "rgba(224,230,33,0.14)")}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
                data-testid={`branch-option-${b.code}`}
              >
                <Building2 size={14} style={{ color: "#0A3747" }} />
                <div className="flex-1">
                  <div style={{ color: "#0A3747" }} className="font-medium">{b.name}</div>
                  <div className="text-[10px] font-mono uppercase" style={{ color: "#94A3B8" }}>{b.code}</div>
                </div>
                {activeBranchId === b.id && <Check size={14} style={{ color: "#0F766E" }} />}
              </button>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
